/*
<purpose>
  THE cross-window bus, one BroadcastChannel for every fact two Smarttix windows owe each other.
  Two facts ride it today, discriminated by `type`:
    ticket-mutated   — any window that successfully mutates a ticket announces the uuid; every
                       OTHER window refetches what it shows (detail refetch + silent list
                       refresh). uuid '*' = broad signal for bulk operations ("lists changed").
    draft-disclosure — a strip a clerk expanded (services/draftDisclosure, 2026-09-17). It
                       carries its PAYLOAD rather than a "go look" signal, because the store
                       behind it is sessionStorage and a pop-out holds its own COPY of that.
</purpose>
<dependencies>BroadcastChannel (browser). No app imports — leaf module.</dependencies>
<constraints>
  - ONE channel name. A second fact gets a `type`, never a second channel: two channels are two
    lifecycles, two feature-detections and two silent failures on the browser that has neither.
  - Must be a silent no-op where BroadcastChannel is unsupported (jsdom, ancient browsers);
    convergence then falls back to the existing poll interval. Never throw.
  - BroadcastChannel never echoes to the posting window — announcing is inherently
    self-loop-free. Do not "fix" that by also invoking local listeners; a caller that needs its
    own window told does that at its own level (see services/draftDisclosure).
</constraints>
*/

const CHANNEL_NAME = 'stx-ticket-sync';

interface TicketMutatedMessage { type: 'ticket-mutated'; uuid: string; }
interface DraftDisclosureMessage {
  type: 'draft-disclosure';
  uuid: string;
  /** The draft slot kind (services/draftClaims DraftSlotKind) — typed as a string HERE so this
   *  leaf module keeps no app imports; the store that reads it owns the narrowing. */
  kind: string;
  on: boolean;
}

let channel: BroadcastChannel | null = null;
const listeners = new Set<(uuid: string) => void>();
const disclosureListeners = new Set<(uuid: string, kind: string, on: boolean) => void>();

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (ev: MessageEvent) => {
      // Read as a bag of unknowns and NARROWED here, rather than cast to one of the two shapes:
      // the wire carries whatever another window (or another version of this app) posted, so the
      // discriminator and every field it promises are checked before either list is called.
      const msg = ev.data as { type?: unknown; uuid?: unknown; kind?: unknown; on?: unknown } | null;
      if (!msg || typeof msg.uuid !== 'string') return;
      const uuid = msg.uuid;
      if (msg.type === 'ticket-mutated') {
        listeners.forEach(cb => cb(uuid));
        return;
      }
      if (msg.type === 'draft-disclosure' && typeof msg.kind === 'string' && typeof msg.on === 'boolean') {
        const { kind, on } = msg;
        disclosureListeners.forEach(cb => cb(uuid, kind, on));
      }
    };
  }
  return channel;
}

export function announceTicketMutated(uuid: string): void {
  const msg: TicketMutatedMessage = { type: 'ticket-mutated', uuid };
  getChannel()?.postMessage(msg);
}

export function onTicketMutated(cb: (uuid: string) => void): () => void {
  getChannel(); // ensure the message handler exists before first delivery
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Tell the other windows that one strip's disclosure changed — see services/draftDisclosure. */
export function announceDraftDisclosure(uuid: string, kind: string, on: boolean): void {
  const msg: DraftDisclosureMessage = { type: 'draft-disclosure', uuid, kind, on };
  getChannel()?.postMessage(msg);
}

/** Hear the other windows' disclosure changes — see services/draftDisclosure. */
export function onDraftDisclosure(
  cb: (uuid: string, kind: string, on: boolean) => void,
): () => void {
  getChannel(); // ensure the message handler exists before first delivery
  disclosureListeners.add(cb);
  return () => { disclosureListeners.delete(cb); };
}
